// src/services/purchasesAPI.ts
// Purchase tracking API service for inventory purchase transactions
// Follows same patterns as inventoryAPI.ts for consistency

// 🔐 Centralized auth service
import { authService } from './authService';

// 📊 Centralized logging
import { logger } from '../utils/logger';

// 🔧 Environment configuration
import { getApiBaseUrl } from './configService';

// 📦 Purchase types
import {
  Purchase,
  CreatePurchaseRequest,
  UpdatePurchaseRequest,
  PurchaseHistoryResponse,
  PurchaseFilters
} from '../types/purchase';

// Create service-specific logger
const purchasesLogger = logger.createServiceLogger('PurchasesAPI');

// Types for API responses
export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// API service class
export class PurchasesAPI {
  private baseUrl: string;

  constructor() {
    // Get base URL from environment configuration
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * 🚀 Uses centralized authService for all requests
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('PurchasesAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('PurchasesAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    // If we're sending JSON and no header is set, add it
    const hasBody = options.body !== undefined && options.body !== null;
    const headers = new Headers(options.headers || {});
    if (hasBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('PurchasesAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : (undefined as unknown as APIResponse<T>);

      if (!response.ok) {
        purchasesLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error:
            (data && (data as any).error) || {
              code: 'HTTP_ERROR',
              message: `HTTP ${response.status}`,
            },
        };
      }

      return (data ?? { success: true }) as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      purchasesLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  // ===== Core Purchase CRUD Operations =====

  /**
   * Create a new purchase record
   */
  async createPurchase(purchaseData: CreatePurchaseRequest): Promise<APIResponse<Purchase>> {
    purchasesLogger.info('Creating new purchase record', { 
      itemId: purchaseData.itemId,
      quantity: purchaseData.quantity,
      unitCost: purchaseData.unitCost,
      vendor: purchaseData.vendor 
    });
    
    return this.makeRequest<Purchase>('/purchases', {
      method: 'POST',
      body: JSON.stringify(purchaseData),
    });
  }

  /**
   * Get all purchases for tenant (with optional filters)
   */
  async getPurchases(filters?: PurchaseFilters): Promise<APIResponse<Purchase[]>> {
    purchasesLogger.debug('Fetching purchases', { filters });

    const params = new URLSearchParams();
    if (filters) {
      if (filters.itemId) params.append('itemId', filters.itemId);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/purchases?${queryString}` : '/purchases';
    
    return this.makeRequest<Purchase[]>(endpoint);
  }

  /**
   * Get a single purchase by ID
   */
  async getPurchase(purchaseId: string): Promise<APIResponse<Purchase>> {
    purchasesLogger.debug('Fetching purchase', { purchaseId });
    
    return this.makeRequest<Purchase>(`/purchases/${encodeURIComponent(purchaseId)}`);
  }

  /**
   * Update an existing purchase record
   */
  async updatePurchase(
    purchaseId: string,
    updates: UpdatePurchaseRequest
  ): Promise<APIResponse<Purchase>> {
    purchasesLogger.info('Updating purchase record', { 
      purchaseId,
      fieldsUpdated: Object.keys(updates)
    });
    
    return this.makeRequest<Purchase>(`/purchases/${encodeURIComponent(purchaseId)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a purchase record
   */
  async deletePurchase(purchaseId: string): Promise<APIResponse<void>> {
    purchasesLogger.warn('Deleting purchase record', { purchaseId });
    
    return this.makeRequest<void>(`/purchases/${encodeURIComponent(purchaseId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Increment allocated quantity when items are created from purchase
   * This tracks how many units have been added to inventory
   */
  async incrementAllocatedQuantity(
    purchaseId: string,
    incrementBy: number
  ): Promise<APIResponse<Purchase>> {
    purchasesLogger.info('Incrementing allocated quantity', { 
      purchaseId,
      incrementBy
    });

    // First get current purchase to calculate new allocatedQuantity
    const currentPurchase = await this.getPurchase(purchaseId);
    if (!currentPurchase.success || !currentPurchase.data) {
      return currentPurchase as APIResponse<Purchase>;
    }

    const currentAllocated = currentPurchase.data.allocatedQuantity || 0;
    const newAllocated = currentAllocated + incrementBy;

    // Update with new allocated quantity
    return this.updatePurchase(purchaseId, {
      allocatedQuantity: newAllocated
    });
  }

  // ===== Purchase History & Analytics =====

  /**
   * Get purchase history for a specific item (with totals)
   */
  async getPurchaseHistory(itemId: string): Promise<APIResponse<PurchaseHistoryResponse>> {
    purchasesLogger.debug('Fetching purchase history for item', { itemId });
    
    return this.makeRequest<PurchaseHistoryResponse>(`/purchases/item/${encodeURIComponent(itemId)}`);
  }

  /**
   * Get purchases by date range
   */
  async getPurchasesByDateRange(
    startDate: string,
    endDate: string
  ): Promise<APIResponse<Purchase[]>> {
    purchasesLogger.debug('Fetching purchases by date range', { startDate, endDate });
    
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    
    return this.makeRequest<Purchase[]>(`/purchases?${params.toString()}`);
  }

  /**
   * Get purchases by category
   */
  async getPurchasesByCategory(categoryId: string): Promise<APIResponse<Purchase[]>> {
    purchasesLogger.debug('Fetching purchases by category', { categoryId });
    
    return this.makeRequest<Purchase[]>(`/purchases?categoryId=${encodeURIComponent(categoryId)}`);
  }

  /**
   * Get purchases by vendor
   */
  async getPurchasesByVendor(vendor: string): Promise<APIResponse<Purchase[]>> {
    purchasesLogger.debug('Fetching purchases by vendor', { vendor });
    
    const params = new URLSearchParams();
    params.append('vendor', vendor);
    
    return this.makeRequest<Purchase[]>(`/purchases?${params.toString()}`);
  }

  // ===== Bulk Operations =====

  /**
   * Create multiple purchase records (for bulk restocking)
   */
  async bulkCreatePurchases(
    purchases: CreatePurchaseRequest[]
  ): Promise<APIResponse<{ created: Purchase[]; errors: Array<{ index: number; error: string }> }>> {
    purchasesLogger.info('Bulk creating purchase records', { 
      purchaseCount: purchases.length
    });
    
    return this.makeRequest('/purchases/bulk', {
      method: 'POST',
      body: JSON.stringify({ purchases }),
    });
  }

  /**
   * Delete multiple purchase records
   */
  async bulkDeletePurchases(
    purchaseIds: string[]
  ): Promise<APIResponse<{ deleted: string[]; errors: Array<{ purchaseId: string; error: string }> }>> {
    purchasesLogger.warn('Bulk deleting purchase records', { 
      purchaseCount: purchaseIds.length
    });
    
    return this.makeRequest('/purchases/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ purchaseIds }),
    });
  }

  // ===== Export Operations =====

  /**
   * Export purchase data to CSV
   */
  async exportPurchases(filters?: PurchaseFilters): Promise<Blob> {
    purchasesLogger.info('Exporting purchase data', { filters });

    const params = new URLSearchParams();
    if (filters) {
      if (filters.itemId) params.append('itemId', filters.itemId);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/purchases/export?${queryString}` : '/purchases/export';

    const response = await authService.makeAuthenticatedRequest(
      `${this.baseUrl}${endpoint}`
    );

    if (!response.ok) {
      purchasesLogger.error('Export failed', { status: response.status, statusText: response.statusText });
      throw new Error(`Export failed: ${response.statusText}`);
    }

    purchasesLogger.info('Export completed successfully');
    return response.blob();
  }

  // ===== Analytics & Reporting =====

  /**
   * Get purchase summary statistics
   */
  async getPurchaseSummary(filters?: PurchaseFilters): Promise<APIResponse<{
    totalPurchases: number;
    totalQuantity: number;
    totalCost: number;
    averageUnitCost: number;
    byCategory: Record<string, { count: number; totalCost: number }>;
    byVendor: Record<string, { count: number; totalCost: number }>;
    recentPurchases: Purchase[];
  }>> {
    purchasesLogger.debug('Fetching purchase summary', { filters });
    
    const params = new URLSearchParams();
    if (filters) {
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/purchases/summary?${queryString}` : '/purchases/summary';
    
    return this.makeRequest(endpoint);
  }

  /**
   * Get top vendors by spend
   */
  async getTopVendors(limit: number = 10): Promise<APIResponse<Array<{
    vendor: string;
    totalSpend: number;
    purchaseCount: number;
    averageOrderValue: number;
  }>>> {
    purchasesLogger.debug('Fetching top vendors', { limit });
    
    return this.makeRequest(`/purchases/top-vendors?limit=${limit}`);
  }

  /**
   * Get spending trends by month
   */
  async getSpendingTrends(months: number = 12): Promise<APIResponse<Array<{
    month: string;
    totalSpend: number;
    purchaseCount: number;
    averageCost: number;
  }>>> {
    purchasesLogger.debug('Fetching spending trends', { months });
    
    return this.makeRequest(`/purchases/spending-trends?months=${months}`);
  }
}

// Export singleton instance
export const purchasesAPI = new PurchasesAPI();
